import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { useGetWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { canManageMembers } from '../utils/canManageMembers';
import type { Member as TeamMember, MemberRole } from '../utils/constants';
import { ROUTE_TEAM } from '../utils/constants';

import { MultiLEEditTray } from './MultiLEEditTray';
import { SingleLEEditTray } from './SingleLEEditTray';

export function EditMember() {
  const { user_uuid: userUuid } = useParams<{ user_uuid: string }>();
  const navigate = useNavigate();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';

  const orgMembersData = useGetWorkspaceMembers(
    { organizationId },
    { skip: organizationId.length === 0 },
  );

  const rawMember = useMemo(
    () => orgMembersData?.members?.find((m) => m.userUuid === userUuid) ?? null,
    [orgMembersData, userUuid],
  );

  const canEdit = useMemo(
    () => canManageMembers(orgMembersData?.members?.[0]),
    [orgMembersData?.members],
  );

  const memberStatus = useMemo<'active' | 'pending'>(() => {
    const isActiveMember = (orgMembersData?.members ?? []).some(
      (member) => member.userUuid === userUuid,
    );
    if (isActiveMember) {
      return 'active';
    }

    const isPendingInvite = (orgMembersData?.pendingInvites ?? []).some(
      (invite) => invite.userUuid === userUuid,
    );
    return isPendingInvite ? 'pending' : 'active';
  }, [orgMembersData?.members, orgMembersData?.pendingInvites, userUuid]);

  const teamMember = useMemo<TeamMember | null>(() => {
    if (!rawMember) {
      return null;
    }
    return {
      userUuid: rawMember.userUuid ?? '',
      firstName: rawMember.firstName ?? '',
      lastName: rawMember.lastName ?? '',
      email: rawMember.email ?? '',
      roles: (rawMember.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ?? []) as MemberRole[],
      avatarUrl: rawMember.avatarUrl,
    };
  }, [rawMember]);

  const handleClose = useCallback(() => {
    logEvent('member_info_page_closed', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });
    navigate(ROUTE_TEAM);
  }, [navigate]);

  useEffect(() => {
    logEvent('member_info_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  // Redirect if organizationId is missing
  useEffect(() => {
    if (!organizationId) {
      navigate({ pathname: ROUTE_TEAM }, { replace: true });
    }
  }, [organizationId, navigate]);

  // Redirect if member not found once data has loaded
  useEffect(() => {
    if (orgMembersData && !rawMember) {
      navigate(ROUTE_TEAM, { replace: true });
    }
  }, [navigate, orgMembersData, rawMember]);

  if (!teamMember) {
    return (
      <Box alignItems="center" justifyContent="center" padding={6}>
        <Spinner size={4} color="fgPrimary" />
      </Box>
    );
  }

  const legalEntityCount = selectedOrganization?.legalEntities?.length ?? 0;

  if (legalEntityCount > 1) {
    return (
      <MultiLEEditTray
        member={teamMember}
        memberLegalEntityRoles={rawMember?.legalEntityRoles}
        status={memberStatus}
        isEditable={canEdit}
        onClose={handleClose}
      />
    );
  }

  return (
    <SingleLEEditTray
      member={teamMember}
      status={memberStatus}
      isEditable={canEdit}
      onClose={handleClose}
    />
  );
}
